/* Alberts mega-menu: desktop dropdown controller with hover-intent + sticky scroll-up.
   Plain web component (no theme-framework coupling).
   - Nav triggers live in .rmh__nav ([data-rmh-trigger][data-menu-title][aria-controls]).
   - Panels are this element's children ([data-rmh-panel][data-menu-title]).
*/
const OPEN_DELAY = 70;
const CLOSE_DELAY = 200;

class RobertsMegaMenu extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.rmh') || document;
    this.openTimer = null;
    this.closeTimer = null;
    this.activeTitle = null;

    this.triggers = Array.from(
      this.root.querySelectorAll('[data-rmh-trigger][aria-controls]')
    );

    this.triggers.forEach((trigger) => {
      const title = trigger.getAttribute('data-menu-title');
      trigger.addEventListener('pointerenter', () => this.scheduleOpen(title));
      trigger.addEventListener('pointerleave', () => this.scheduleClose());
      trigger.addEventListener('focus', () => this.open(title));
      // Let keyboard/touch users follow the link; only block default when opening via hover is irrelevant.
    });

    this.addEventListener('pointerenter', () => this.cancelClose());
    this.addEventListener('pointerleave', () => this.scheduleClose());

    this._onKeydown = (e) => { if (e.key === 'Escape') this.closeNow(); };
    this._onClickOutside = (e) => {
      if (this.dataset.open === 'true' && this.root instanceof Element && !this.root.contains(e.target)) {
        this.closeNow();
      }
    };
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('pointerdown', this._onClickOutside, true);

    this.#initSticky();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('pointerdown', this._onClickOutside, true);
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this._cancelStickyFrame?.();
    this.#clearTimers();
  }

  #clearTimers() {
    if (this.openTimer) { clearTimeout(this.openTimer); this.openTimer = null; }
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
  }

  panelFor(title) {
    if (!title) return null;
    return this.querySelector(`[data-rmh-panel][data-menu-title="${CSS.escape(title)}"]`);
  }

  scheduleOpen(title) {
    this.cancelClose();
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => this.open(title), OPEN_DELAY);
  }

  scheduleClose() {
    if (this.openTimer) { clearTimeout(this.openTimer); this.openTimer = null; }
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeNow(), CLOSE_DELAY);
  }

  cancelClose() {
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
  }

  open(title) {
    const panel = this.panelFor(title);
    if (!panel) { this.closeNow(); return; }
    if (this.activeTitle === title && this.dataset.open === 'true') return;

    // Hide any currently-open panel
    this.querySelectorAll('[data-rmh-panel]').forEach((p) => {
      if (p !== panel) p.setAttribute('aria-hidden', 'true');
    });
    panel.setAttribute('aria-hidden', 'false');

    // Measure and expand the host
    const h = panel.scrollHeight;
    this.style.setProperty('--rmh-panel-height', `${h}px`);
    this.dataset.open = 'true';
    this.activeTitle = title;

    this.triggers.forEach((t) => {
      t.setAttribute('aria-expanded', String(t.getAttribute('data-menu-title') === title));
    });
  }

  closeNow() {
    this.#clearTimers();
    this.dataset.open = 'false';
    this.activeTitle = null;
    this.querySelectorAll('[data-rmh-panel]').forEach((p) => p.setAttribute('aria-hidden', 'true'));
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  /* Sticky bar.
     'scroll-up' pins the header only while the visitor is scrolling up, 'always' pins it
     for the whole page. Both work the same way: the bar stays in the flow until it has
     been scrolled past, then it's switched to position: fixed (see the sticky block in
     roberts-mega-header.css) and its section wrapper takes over the space it left, so the
     page below doesn't jump. The announcement bar above it is left behind on purpose --
     only the header itself follows the visitor. */
  #initSticky() {
    const host = this.root instanceof Element ? this.root : null;
    if (!host) return;
    const mode = host.getAttribute('data-sticky');
    if (mode !== 'scroll-up' && mode !== 'always') return;

    // The section wrapper (.rmh-section) holds the bar's height while it's out of the flow.
    const wrapper = host.parentElement;
    // Ignore scroll jitter below this many pixels, so a trackpad wobble doesn't flip the bar.
    const DIRECTION_THRESHOLD = 4;

    let state = 'none';
    let naturalTop = 0;
    let naturalHeight = 0;
    let lastY = window.scrollY;
    let rafId = null;

    // Only meaningful while the bar is still in the flow; once fixed, offsets are viewport-based.
    const measure = () => {
      if (state !== 'none') return;
      naturalHeight = host.offsetHeight;
      naturalTop = host.getBoundingClientRect().top + window.scrollY;
    };

    const setState = (next) => {
      if (next === state) return;

      if (next === 'none') {
        host.removeAttribute('data-sticky-state');
        host.removeAttribute('data-sticky-animate');
        if (wrapper) wrapper.style.removeProperty('min-height');
        state = next;
        measure();
        return;
      }

      // Reserve the space first, then detach, so the two happen in the same frame.
      if (wrapper) wrapper.style.minHeight = `${naturalHeight}px`;
      host.setAttribute('data-sticky-animate', next === 'visible' || state === 'visible' ? 'true' : 'false');
      host.setAttribute('data-sticky-state', next);
      state = next;
      // A dropdown left hanging off a bar that's sliding away looks broken.
      if (next === 'hidden') this.closeNow();
    };

    const update = () => {
      rafId = null;
      const y = Math.max(window.scrollY, 0);
      const delta = y - lastY;

      if (y <= naturalTop) {
        setState('none');
        lastY = y;
        return;
      }

      if (mode === 'always') {
        setState('visible');
      } else if (delta > DIRECTION_THRESHOLD) {
        setState('hidden');
      } else if (delta < -DIRECTION_THRESHOLD) {
        setState('visible');
      }

      // Keep the reference point until the threshold is crossed, so slow scrolls still count.
      if (Math.abs(delta) > DIRECTION_THRESHOLD) lastY = y;
    };

    const schedule = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    this._onScroll = schedule;
    this._onResize = () => {
      if (state === 'none') {
        measure();
      } else if (wrapper) {
        wrapper.style.minHeight = `${host.offsetHeight}px`;
      }
    };
    this._cancelStickyFrame = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };

    measure();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    update();
  }
}

if (!customElements.get('roberts-mega-menu')) {
  customElements.define('roberts-mega-menu', RobertsMegaMenu);
}
